// AssistText — renders a string with Kitchen Assist help links (README
// §12.3). Words that resolve through the injected matcher become inline,
// underlined accent buttons; everything else renders verbatim. Shared by
// every text surface that wants assist (task titles via TaskRectangle, the
// derived PrepCook checklist rows), so the linkify logic lives in one place.
//
// Presentational and matcher-agnostic: the cooking layer owns the matcher
// (cooking/assist.ts) and the click handling; this component only splits
// and decorates. When no matcher/handler is supplied it returns the plain
// text, so non-assist callers keep an identical DOM.

import { Fragment, type ReactNode } from 'react';
import { theme } from '../theme';

/**
 * Structural matcher the cooking layer injects to light up help words.
 * Kept minimal and local so the design layer stays independent of the
 * cooking module, mirroring how NotePopover defines its own entry type.
 */
export interface TaskAssistMatcher {
  /** Canonical help word for a token, or null if it has no glossary entry. */
  match(token: string): string | null;
}

// Inline help link: a matched word as an underlined accent button.
// `stopPropagation` keeps the click off any surrounding checkbox / chef-hat.
function AssistLink({ word, onClick }: { word: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="cc-assist-link"
      aria-label={`Help with ${word}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        font: 'inherit',
        color: theme.accent.action,
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        cursor: 'pointer',
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
      }}
    >
      {word}
    </button>
  );
}

export interface AssistTextProps {
  text: string;
  matcher?: TaskAssistMatcher;
  onAssistWord?: (canonicalWord: string) => void;
}

export function AssistText({ text, matcher, onAssistWord }: AssistTextProps): ReactNode {
  if (!matcher || !onAssistWord) return text;
  // Split into alternating non-word / word runs; odd indices are words.
  const parts = text.split(/([\p{L}\p{N}]+)/u);
  let matched = false;
  const out = parts.map((part, i) => {
    if (i % 2 === 1) {
      const canonical = matcher.match(part);
      if (canonical) {
        matched = true;
        return <AssistLink key={i} word={part} onClick={() => onAssistWord(canonical)} />;
      }
    }
    return part ? <Fragment key={i}>{part}</Fragment> : null;
  });
  return matched ? out : text;
}

export default AssistText;
